// Shapes as SVG path data in their box (0,0)–(w,h). The canvas paints them with Path2D and
// the panels preview them as <svg>, from the same path. A closed shape's stroke is drawn
// inset by half its width, so the outline stays inside the box the selection shows.
const f = value => Math.round(value * 100) / 100;

function polygon(points) {
  return `${points.map(([x, y], index) => `${index ? 'L' : 'M'}${f(x)} ${f(y)}`).join(' ')} Z`;
}

function roundedRect(x, y, w, h, radius) {
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));
  if (!r) return `M${f(x)} ${f(y)} H${f(x + w)} V${f(y + h)} H${f(x)} Z`;
  return `M${f(x + r)} ${f(y)} H${f(x + w - r)} A${f(r)} ${f(r)} 0 0 1 ${f(x + w)} ${f(y + r)} V${f(y + h - r)} A${f(r)} ${f(r)} 0 0 1 ${f(x + w - r)} ${f(y + h)}`
    + ` H${f(x + r)} A${f(r)} ${f(r)} 0 0 1 ${f(x)} ${f(y + h - r)} V${f(y + r)} A${f(r)} ${f(r)} 0 0 1 ${f(x + r)} ${f(y)} Z`;
}

function ellipse(x, y, w, h) {
  const rx = w / 2;
  const ry = h / 2;
  return `M${f(x)} ${f(y + ry)} A${f(rx)} ${f(ry)} 0 1 0 ${f(x + w)} ${f(y + ry)} A${f(rx)} ${f(ry)} 0 1 0 ${f(x)} ${f(y + ry)} Z`;
}

function radial(x, y, w, h, count, inner = 1, start = -Math.PI / 2) {
  const points = [];
  const steps = inner === 1 ? count : count * 2;
  for (let i = 0; i < steps; i++) {
    const angle = start + (i * 2 * Math.PI) / steps;
    const scale = inner !== 1 && i % 2 ? inner : 1;
    points.push([x + w / 2 + Math.cos(angle) * (w / 2) * scale, y + h / 2 + Math.sin(angle) * (h / 2) * scale]);
  }
  return polygon(points);
}

export function shapePath(shape, width, height, { cornerRadius = 0, strokeWidth = 0 } = {}) {
  const inset = shape === 'line' ? 0 : strokeWidth / 2;
  const x = inset;
  const y = inset;
  const w = Math.max(0.5, width - inset * 2);
  const h = Math.max(0.5, height - inset * 2);
  switch (shape) {
    case 'ellipse': return ellipse(x, y, w, h);
    case 'triangle': return polygon([[x + w / 2, y], [x + w, y + h], [x, y + h]]);
    case 'diamond': return polygon([[x + w / 2, y], [x + w, y + h / 2], [x + w / 2, y + h], [x, y + h / 2]]);
    case 'star': return radial(x, y, w, h, 5, 0.46);
    case 'hexagon': return radial(x, y, w, h, 6, 1, -Math.PI / 2);
    case 'line': return `M0 ${f(height / 2)} L${f(width)} ${f(height / 2)}`;
    case 'arrow': {
      const head = Math.min(w * 0.42, h * 1.1);
      const shaft = h * 0.34;
      return polygon([[x, y + h / 2 - shaft / 2], [x + w - head, y + h / 2 - shaft / 2], [x + w - head, y], [x + w, y + h / 2],
        [x + w - head, y + h], [x + w - head, y + h / 2 + shaft / 2], [x, y + h / 2 + shaft / 2]]);
    }
    default: return roundedRect(x, y, w, h, Math.max(0, cornerRadius - inset));
  }
}

// The rounded rectangle images and highlights are clipped to.
export const roundedRectPath = (width, height, radius) => roundedRect(0, 0, width, height, radius);

// Shapes that can be resized freely keep no ratio; the rest default to their square box.
export const SQUARE_SHAPES = ['ellipse', 'star', 'hexagon', 'diamond', 'triangle'];
